import { useEffect, useRef, useState } from "react";

const useLazyImage = (src) => {
  const [loadedSrc, setLoadedSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!src) return;

    setIsLoading(true);
    setLoadedSrc(null);

    let observer;
    const element = ref.current;

    if (element && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = new Image();

              img.onload = () => {
                setLoadedSrc(src);
                setIsLoading(false);
                setHasError(false);
              };

              img.onerror = () => {
                setHasError(true);
                setIsLoading(false);
              };

              img.src = src;
              observer.unobserve(element);
            }
          });
        },
        { rootMargin: "100px", threshold: 0.01 }
      );

      observer.observe(element);
    } else {
      const img = new Image();
      img.onload = () => {
        setLoadedSrc(src);
        setIsLoading(false);
      };
      img.onerror = () => {
        setHasError(true);
        setIsLoading(false);
      };
      img.src = src;
    }

    return () => {
      if (observer && element) {
        observer.unobserve(element);
      }
    };
  }, [src]);

  return { ref, loadedSrc, isLoading, hasError };
};

// For use in components
export const LazyImageComponent = ({
  src,
  alt = "",
  className = "",
  style = {},
  onLoad,
  onError,
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef(null);

  useEffect(() => {
    let observer;
    const imgElement = imgRef.current;

    if (imgElement && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = new Image();

              img.onload = () => {
                setImageSrc(src);
                setIsLoading(false);
                if (onLoad) onLoad();
              };

              img.onerror = () => {
                setIsLoading(false);
                if (onError) onError();
              };

              img.src = src;
              observer.unobserve(imgElement);
            }
          });
        },
        { rootMargin: "50px" }
      );

      observer.observe(imgElement);
    } else {
      setImageSrc(src);
      setIsLoading(false);
    }

    return () => {
      if (observer && imgElement) {
        observer.unobserve(imgElement);
      }
    };
  }, [src, onLoad, onError]);

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={className}
      style={{
        ...style,
        opacity: isLoading ? 0 : 1,
        transition: "opacity 0.3s ease-in-out",
      }}
    />
  );
};

export default useLazyImage;
